import React, { useState } from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Modal from "@mui/material/Modal";
import BSSelect from "../../../common/js/components/BSSelect";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import operations from "./ducks/operations";
import { quarters, buildingClasses, buildingStatuses } from "../utils/constants";
import {
    yearsForExportResearch,
    marketAreasForResearchSnapshot,
    submarketOptionsForResearch,
    micromarketOptionsForResearch,
    propertyTypeFilterOptionsForExportResearch,
    specialtyTypeFilterOptionsForExportResearch,
    endYearsForExportResearch,
    researchExportDateValidation,
} from "../property/constants";

const HeaderText = styled("h4")(({ theme }) => ({
    marginTop: "45px",
    textAlign: "left",
    fontSize: "18px",
    marginLeft: "24px",
}));

const ModalContainer = styled("div")(({ theme }) => ({
    margin: "0 auto",
    marginTop: "10%",
    position: "relative",
    alignItems: "center",
    borderRadius: "3px",
    "&:focus": {
        outline: "none",
    },
    width: "525px",
    height: "675px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    overflowY: "auto",
}));

const SelectionColumn = styled("div")({
    margin: "24px 24px 0px 24px",
});

const RowContainer = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
    margin: "-1px",
    boxSizing: "border-box"
});

const WidthFixItem = styled("div")({
    flexGrow: "1",
    flexBasis: "0",
    maxWidth: "100%",
    boxSizing: "border-box"
});

const SmallWidthItem = styled("div")({
    flex: "1 0 auto",
    padding: "8px",
    marginRight: "0px",
});

const FullWidthItem = styled("div")({
    maxWidth: "100%",
    boxSizing: "border-box"
});

const FlexDirectionRow = styled(RadioGroup)({
    flexDirection: "row",
});

const ButtonContainer = styled("div")({
    position: "relative",
    display: "flex",
    justifyContent: "flex-end",
    margin: "0vw 1vw 1vw 0vw",
});

const ResearchExportModal = (props) => {
    const { closeModal, modalOpen, snapshotResponse } = props;

    const [sending, setSending] = useState(false);
    const [startYear, setStartYear] = useState("Select");
    const [endYear, setEndYear] = useState("Select");
    const [startQuarter, setStartQuarter] = useState("Select");
    const [endQuarter, setEndQuarter] = useState("Select");
    const [marketArea, setMarketArea] = useState("Select");
    const [subMarketArea, setSubMarketArea] = useState("All");
    const [microMarketArea, setMicroMarketArea] = useState("All");
    const [propertyType, setPropertyType] = useState("Select");
    const [propertySubType, setPropertySubType] = useState("All");
    const [status, setStatus] = useState("All");
    const [propertyClass, setPropertyClass] = useState("All");
    const [tracking, setTracking] = useState("Tracked Properties Only");

    const onExitModal = () => {
        setSending(false);
        setStartYear("Select");
        setEndYear("Select");
        setStartQuarter("Select");
        setEndQuarter("Select");
        setMarketArea("Select");
        setSubMarketArea("All");
        setMicroMarketArea("All");
        setPropertyType("Select");
        setPropertySubType("All");
        setStatus("All");
        setPropertyClass("All");
        setTracking("Tracked Properties Only");
        closeModal();
    };

    const onSubmit = async () => {
        setSending(true);
        const exportPayload = {
            searchCriteria: {
                researchCriteria: {
                    startQuarter: startQuarter,
                    startYear: startYear,
                    endQuarter: endQuarter,
                    endYear: endYear,
                },
                locationCriteria: {
                    states: null,
                    zips: null,
                    counties: null,
                    cities: null,
                    markets: {
                        [marketArea]: {
                            ...(subMarketArea !== "All" && {
                                [subMarketArea]: [...(microMarketArea !== "All" ? [microMarketArea] : [])],
                            }),
                        },
                    },
                },
                sizeCriteria: {
                    availableSf: false,
                    totalSf: false,
                    acreage: false,
                    totalTransactionSf: false,
                },
                propertyTypes: {
                    [propertyType]: {
                        selected: true,
                        buildingClassCriteria: {
                            a: propertyClass === "All" || propertyClass === "A",
                            b: propertyClass === "All" || propertyClass === "B",
                            c: propertyClass === "All" || propertyClass === "C",
                            d: propertyClass === "All" || propertyClass === "D",
                            trophy: propertyClass === "All" || propertyClass === "Trophy",
                        },
                        specialtyTypeCriteria: {
                            ...(propertySubType !== "All" && {
                                [propertySubType]: true,
                            }),
                        },
                    },
                },
                statusCriteria: {
                    tracked: tracking === "Tracked Properties Only",
                    ...(status !== "All" && {
                        [status.toLowerCase()]: true,
                    }),
                },
                offices: [],
            },
            startIndex: 0,
            requestId: new Date().valueOf() + "",
            numResults: 300,
        };
        const response = await operations.exportResearch(exportPayload);
        if (response.status >= 200 && response.status < 300) {
            snapshotResponse(true);
            window.open(response.data, "_blank");
        } else {
            snapshotResponse(false);
        }
        onExitModal();
    };

    const handleStartQuarterUpdate = (event) => {
        setStartQuarter(event.target.value);
    };

    const handleStartYearUpdate = (event) => {
        setStartYear(event.target.value);
    };

    const handleEndQuarterUpdate = (event) => {
        setEndQuarter(event.target.value);
    };

    const handleEndYearUpdate = (event) => {
        setEndYear(event.target.value);
    };

    const handleMarketAreaUpdate = (event) => {
        setMarketArea(event.target.value);
        setSubMarketArea("All");
        setMicroMarketArea("All");
    };

    const handleSubMarketAreaUpdate = (event) => {
        setSubMarketArea(event.target.value);
        setMicroMarketArea("All");
    };

    const handleMicroMarketAreaUpdate = (event) => {
        setMicroMarketArea(event.target.value);
    };

    const handlePropertyTypeUpdate = (event) => {
        setPropertyType(event.target.value);
        setPropertyClass(false);
    };

    const handlePropertySubTypeUpdate = (event) => {
        setPropertySubType(event.target.value);
    };

    const handleStatusUpdate = (event) => {
        setStatus(event.target.value);
    };

    const handlePropertyClassUpdate = (event) => {
        setPropertyClass(event.target.value);
    };

    const handleTrackingToggle = (event) => {
        setTracking(event);
    };

    return (
        <Modal
            open={modalOpen}
            onClose={onExitModal}
            style={{
                height: "60vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "10vh",
            }}
        >
            <ModalContainer>
                <HeaderText>Research Export</HeaderText>
                <SelectionColumn>
                    <RowContainer>
                        <WidthFixItem style={{ marginRight: '90px'}}>
                            <BSSelect
                                id="startQuarterSelect"
                                label="Start Quarter"
                                value={startQuarter}
                                onChange={handleStartQuarterUpdate}
                                placeholder="Select a quarter"
                                options={quarters}
                                disabled={false}
                            />
                        </WidthFixItem>
                        <WidthFixItem>
                            <BSSelect
                                id="startYearSelect"
                                label="Start Year"
                                value={startYear}
                                onChange={handleStartYearUpdate}
                                placeholder="Select a start year"
                                options={yearsForExportResearch()}
                                disabled={false}
                            />
                        </WidthFixItem>
                    </RowContainer>
                    <RowContainer>
                        <WidthFixItem style={{ marginRight: '90px'}}>
                            <BSSelect
                                id="endQuarterSelect"
                                label="End Quarter"
                                value={endQuarter}
                                onChange={handleEndQuarterUpdate}
                                placeholder="Select a end quarter"
                                options={quarters}
                                disabled={false}
                            />
                        </WidthFixItem>
                        <WidthFixItem>
                            <BSSelect
                                id="endYearSelect"
                                label="End Year"
                                value={endYear}
                                onChange={handleEndYearUpdate}
                                placeholder="Select a end year"
                                options={endYearsForExportResearch(startYear)}
                                disabled={false}
                            />
                        </WidthFixItem>
                    </RowContainer>
                    <FullWidthItem>
                        <BSSelect
                            id="marketAreaSelect"
                            label="Market Area"
                            value={marketArea}
                            onChange={handleMarketAreaUpdate}
                            placeholder="Select a market area"
                            options={marketAreasForResearchSnapshot()}
                            disabled={false}
                        />
                    </FullWidthItem>
                    <FullWidthItem>
                        <BSSelect
                            id="subMarketAreaSelect"
                            label="SubMarket"
                            value={subMarketArea}
                            onChange={handleSubMarketAreaUpdate}
                            placeholder="Select a submarket area"
                            options={submarketOptionsForResearch(marketArea)}
                            disabled={marketArea !== "Select" ? false : true}
                        />
                    </FullWidthItem>
                    <FullWidthItem>
                        <BSSelect
                            id="microMarketAreaSelect"
                            label="MicroMarket"
                            value={microMarketArea}
                            onChange={handleMicroMarketAreaUpdate}
                            placeholder="Select a micromarket area"
                            options={micromarketOptionsForResearch(marketArea, subMarketArea)}
                            disabled={subMarketArea !== "All" ? false : true}
                        />
                    </FullWidthItem>
                    <FullWidthItem>
                        <BSSelect
                            id="propertyTypeSelect"
                            label="Property Type"
                            value={propertyType}
                            onChange={handlePropertyTypeUpdate}
                            placeholder="Select a property type"
                            options={propertyTypeFilterOptionsForExportResearch()}
                            disabled={false}
                        />
                    </FullWidthItem>
                    <FullWidthItem>
                        <BSSelect
                            id="propertySubTypeSelect"
                            label="Property SubType"
                            value={propertySubType}
                            onChange={handlePropertySubTypeUpdate}
                            placeholder="Select a property subtype"
                            options={specialtyTypeFilterOptionsForExportResearch(propertyType)}
                            disabled={propertyType !== "Select" ? false : true}
                        />
                    </FullWidthItem>
                    <RowContainer>
                        <WidthFixItem style={{ marginRight: '90px'}}>
                            <BSSelect
                                id="statusSelect"
                                label="Status"
                                value={status}
                                onChange={handleStatusUpdate}
                                placeholder="Select a Status"
                                options={buildingStatuses}
                                disabled={false}
                            />
                        </WidthFixItem>
                        <WidthFixItem>
                            <BSSelect
                                id="classSelect"
                                label="Class"
                                value={propertyClass}
                                onChange={handlePropertyClassUpdate}
                                placeholder="Select a Class"
                                options={buildingClasses}
                                disabled={propertyType !== "Select" ? false : true}
                            />
                        </WidthFixItem>
                    </RowContainer>
                    <FullWidthItem>
                        <FormControl rowcomponent="fieldset">
                            <FormLabel componenet="legend">TRACKING</FormLabel>
                            <FlexDirectionRow
                                aria-label="tracking"
                                name="tracking1"
                                value={tracking}
                                onChange={(e) => handleTrackingToggle(e.target.value)}
                            >
                                <FormControlLabel
                                    value="Tracked Properties Only"
                                    control={<Radio id="include" color="primary" style={{ marginRight: "5px" }} />}
                                    label="Tracked Properties Only"
                                />
                                <FormControlLabel
                                    value="Include Non-Tracked Properties"
                                    control={<Radio id="include" color="primary" />}
                                    label="Include Non-Tracked Properties"
                                />
                            </FlexDirectionRow>
                        </FormControl>
                    </FullWidthItem>
                </SelectionColumn>
                <ButtonContainer>
                    <Button
                        style={{ display: "inline-block", marginLeft: "10px" }}
                        variant="outlined"
                        onClick={() => onExitModal()}
                    >
                        Cancel
                    </Button>
                    <Button
                        style={{ display: "inline-block", marginLeft: "10px" }}
                        variant="contained"
                        color="primary"
                        disabled={
                            startQuarter !== "Select" &&
                            startYear !== "Select" &&
                            endQuarter !== "Select" &&
                            endYear !== "Select" &&
                            marketArea !== "Select" &&
                            propertyType !== "Select" &&
                            sending === false &&
                            researchExportDateValidation(startQuarter, endQuarter, startYear, endYear)
                                ? false
                                : true
                        }
                        onClick={() => onSubmit()}
                    >
                        Export
                    </Button>
                </ButtonContainer>
            </ModalContainer>
        </Modal>
    );
};

export default ResearchExportModal;